import React, { useEffect, useState, useMemo } from 'react';
import './Reportes.css';

function Reportes({ usuario }) {
  const [productos, setProductos] = useState([]);
  const [ventas, setVentas] = useState([]); // nuevas ventas
  const [proximas, setProximas] = useState(null);
  const [ventaSeleccionada, setVentaSeleccionada] = useState(null); // detalle
  const [detalleCargando, setDetalleCargando] = useState(false);
  const [detalleError, setDetalleError] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [accionFila, setAccionFila] = useState({ id:null, tipo:null }); // {id, tipo:'ver'|'imprimir'}
  const [mensajeAccion, setMensajeAccion] = useState(null); // feedback corto

  useEffect(() => {
    const cargar = async () => {
      try {
        setCargando(true);
        setError(null);
        const resp = await fetch('/api/productos');
        if(!resp.ok) throw new Error('No se pudo obtener productos');
        const data = await resp.json();
        setProductos(Array.isArray(data)?data:[]);
        // cargar ventas
        try {
          const rv = await fetch('/api/ventas');
          if (rv.ok) {
            const dv = await rv.json();
            setVentas(Array.isArray(dv)? dv : []);
          }
        } catch(e2){ /* silencioso */ }
        // cargar próximas a vencer (7 días)
        try {
          const rp = await fetch('/api/cotizaciones/proximas/vencer?dias=7');
          if (rp.ok) {
            const dp = await rp.json();
            setProximas(dp);
          }
        } catch(e4){ /* ignore */ }
      } catch(e) {
        setError(e.message);
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, []);

  const fmtNumero = useMemo(()=> new Intl.NumberFormat('es-MX'), []);
  const fmtMoneda = useMemo(()=> new Intl.NumberFormat('es-MX', { style:'currency', currency:'MXN', minimumFractionDigits:2 }), []);

  // Helper para evitar NaN cuando backend devuelve strings, vacío o 'NaN'
  const toNum = (val) => {
    const n = Number(val);
    return Number.isFinite(n) ? n : 0;
  };

  const totalProductos = productos.length;
  const totalStock = productos.reduce((acc,p) => acc + toNum(p.cantidad ?? 0), 0);
  const totalValorInventario = productos.reduce((acc,p) => acc + (toNum(p.precio ?? 0) * toNum(p.cantidad ?? 0)), 0);
  // Métricas de ventas
  const totalVentas = ventas.reduce((acc,v)=> acc + toNum(v.total), 0);
  const ticketPromedio = ventas.length ? (totalVentas / ventas.length) : 0;
  const ventasHoy = (() => {
    const hoy = new Date();
    const y = hoy.getFullYear(), m = hoy.getMonth(), d = hoy.getDate();
    return ventas.filter(v => {
      const f = new Date(v.fecha);
      if (isNaN(f.getTime())) return false;
      return f.getFullYear()===y && f.getMonth()===m && f.getDate()===d;
    });
  })();
  const totalVentasHoy = ventasHoy.reduce((acc,v)=> acc + toNum(v.total), 0);

  // Top 5 por valor (precio * cantidad)
  const topValor = [...productos]
    .map(p => ({
      id: p.id_producto,
      concepto: p.concepto,
      codigo: p.codigo,
      valor: (p.precio ?? 0) * (p.cantidad ?? 0),
      cantidad: p.cantidad ?? 0
    }))
    .sort((a,b)=> b.valor - a.valor)
    .slice(0,5);

  const maxValor = topValor.length ? Math.max(...topValor.map(t=>t.valor)) : 0;

  // Distribución simple por rango de stock
  const rangos = [
    { label:'0', test: (c)=> c===0 },
    { label:'1-5', test: (c)=> c>0 && c<=5 },
    { label:'6-20', test: (c)=> c>=6 && c<=20 },
    { label:'21-50', test: (c)=> c>=21 && c<=50 },
    { label:'+50', test: (c)=> c>50 }
  ];
  const distribucion = rangos.map(r => ({
    label: r.label,
    count: productos.filter(p => r.test(p.cantidad ?? 0)).length
  }));
  const maxDist = distribucion.length ? Math.max(...distribucion.map(d=>d.count)) : 0;

  const cargarDetalle = async (id, { soloRetornar = false } = {}) => {
    // Devuelve los datos para que los botones puedan imprimir inmediatamente
    setDetalleError(null);
    setDetalleCargando(true);
    setAccionFila(prev => ({ ...prev, id }));
    try {
      const r = await fetch(`/api/ventas/${id}`);
      if(!r.ok) throw new Error('No se pudo obtener detalle');
      const data = await r.json();
      if (!soloRetornar) setVentaSeleccionada(data);
      return data;
    } catch(e){
      setDetalleError(e.message);
      setMensajeAccion('Error al obtener detalle: '+ e.message);
      return null;
    } finally {
      setDetalleCargando(false);
      setTimeout(()=> setAccionFila({id:null, tipo:null}), 300);
      setTimeout(()=> setMensajeAccion(null), 4000);
    }
  };

  const cerrarDetalle = () => { setVentaSeleccionada(null); };

  const imprimirTicket = (ventaObj) => {
    if(!ventaObj) return;
    const { venta, detalle } = ventaObj;
    // Formato Ticket Opción A
    // Anchura ~280px, monospace, corte sencillo
    const win = window.open('', 'PRINT', 'height=650,width=400');
    if(!win) return;
    const fecha = new Date(venta.fecha).toLocaleString('es-MX');
    const linea = (txt='') => `<div class="ln">${txt}</div>`;
    const fmt = (n) => new Intl.NumberFormat('es-MX', { style:'currency', currency:'MXN' }).format(Number(n)||0);
    const totalCant = detalle.reduce((a,d)=> a + d.cantidad, 0);
    win.document.write(`<!DOCTYPE html><html><head><title>Ticket #${venta.id_venta}</title>
      <style>
        body{ font-family:monospace; margin:0; padding:10px; }
        .ticket{ width:270px; }
        h1{ font-size:16px; text-align:center; margin:0 0 6px; }
        .center{ text-align:center; }
        .sep{ border-top:1px dashed #000; margin:6px 0; }
        .ln{ display:flex; justify-content:space-between; font-size:12px; }
        .item{ font-size:12px; margin-bottom:4px; }
        .item-name{ display:block; }
        .totales .ln{ font-weight:bold; }
        .small{ font-size:11px; }
      </style>
    </head><body onload="window.print(); setTimeout(()=>window.close(), 400);">
      <div class="ticket">
        <h1>SAFETYPOINT</h1>
        <div class="center small">RFC: N/A</div>
        <div class="center small">Fecha: ${fecha}</div>
        <div class="center small">Folio: ${venta.id_venta}</div>
        <div class="sep"></div>
        ${detalle.map(d=>{
          const unit = (Number(d.precio_unitario) - Number(d.descuento_unitario));
          const imp = unit * d.cantidad;
          return `<div class="item"><span class="item-name">${d.concepto}</span>`+
            `<span>${d.cantidad} x ${unit.toFixed(2)} = ${imp.toFixed(2)}</span></div>`;
        }).join('')}
        <div class="sep"></div>
        <div class="totales">
          ${linea(`Articulos:${totalCant}`)}
          ${linea(`Sub Bruto:${fmt(venta.subtotal_bruto)}`)}
          ${linea(`Desc:${fmt(venta.descuento_total)}`)}
          ${linea(`Sub Neto:${fmt(venta.subtotal_neto)}`)}
          ${linea(`IVA:${fmt(venta.iva)}`)}
          ${linea(`TOTAL:${fmt(venta.total)}`)}
          ${venta.recibido != null ? linea(`Recibido:${fmt(venta.recibido)}`):''}
          ${venta.cambio != null ? linea(`Cambio:${fmt(venta.cambio)}`):''}
          ${linea(`Método:${venta.metodo_pago}`)}
        </div>
        <div class="sep"></div>
        <div class="center small">Gracias por su compra</div>
      </div>
    </body></html>`);
    win.document.close();
    win.focus();
  };

  const imprimirResumenVentas = () => {
    if(!ventas.length) return;
    const win = window.open('', 'PRINT', 'height=750,width=620');
    if(!win) return;
    const fmt = (n)=> new Intl.NumberFormat('es-MX',{style:'currency',currency:'MXN'}).format(Number(n)||0);
    const fechaGen = new Date().toLocaleString('es-MX');
    const total = ventas.reduce((a,v)=> a + (v.total||0),0);
    const promedio = ventas.length ? total/ventas.length : 0;
    win.document.write(`<!DOCTYPE html><html><head><title>Resumen Ventas</title>
      <style>
        body{ font-family:system-ui, Arial, sans-serif; margin:16px; }
        h1{ font-size:18px; margin:0 0 4px; text-align:center; }
        .meta{ text-align:center; font-size:12px; margin-bottom:12px; }
        table{ width:100%; border-collapse:collapse; font-size:11px; }
        th,td{ padding:4px 6px; border-bottom:1px solid #ddd; }
        thead th{ background:#222; color:#fff; position:sticky; top:0; }
        tfoot td{ font-weight:bold; }
        .right{ text-align:right; }
      </style>
    </head><body onload="window.print(); setTimeout(()=>window.close(), 600);">
      <h1>SAFETYPOINT</h1>
      <div class="meta">Resumen de Ventas | Generado: ${fechaGen} | Registros: ${ventas.length}</div>
      <table>
        <thead><tr><th>Folio</th><th>Fecha</th><th>Método</th><th class='right'>Subtotal Neto</th><th class='right'>Desc</th><th class='right'>IVA</th><th class='right'>Total</th></tr></thead>
        <tbody>
          ${ventas.map(v=>`<tr>
            <td>${v.id_venta}</td>
            <td>${new Date(v.fecha).toLocaleString('es-MX')}</td>
            <td>${v.metodo_pago}</td>
            <td class='right'>${fmt(v.subtotal_neto||0)}</td>
            <td class='right'>${fmt(v.descuento_total||0)}</td>
            <td class='right'>${fmt(v.iva||0)}</td>
            <td class='right'>${fmt(v.total||0)}</td>
          </tr>`).join('')}
        </tbody>
        <tfoot>
          <tr><td colspan='6' class='right'>Total Ventas</td><td class='right'>${fmt(total)}</td></tr>
          <tr><td colspan='6' class='right'>Ticket Promedio</td><td class='right'>${fmt(promedio)}</td></tr>
        </tfoot>
      </table>
    </body></html>`);
    win.document.close();
    win.focus();
  };

  const eliminarVenta = async (venta) => {
    if (!venta) return;
    const confirmar = window.confirm(`¿Eliminar la venta #${venta.id_venta}? Esto revertirá el stock.`);
    if(!confirmar) return;
    setAccionFila({ id: venta.id_venta, tipo: 'eliminar' });
    setMensajeAccion(null);
    try {
      const resp = await fetch(`/api/ventas/${venta.id_venta}`, { method: 'DELETE' });
      if(!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || 'No se pudo eliminar');
      }
      // quitar de la lista
      setVentas(prev => prev.filter(v => v.id_venta !== venta.id_venta));
      // recargar productos para reflejar stock devuelto
      try {
        const rp = await fetch('/api/productos');
        if (rp.ok) {
          const dp = await rp.json();
            setProductos(Array.isArray(dp)? dp: []);
        }
      } catch(_e) { /* ignore */ }
      setMensajeAccion(`Venta #${venta.id_venta} eliminada`);
      // cerrar modal si justo estaba abierta esa venta
      if (ventaSeleccionada && ventaSeleccionada.venta.id_venta === venta.id_venta) {
        setVentaSeleccionada(null);
      }
    } catch(e) {
      setMensajeAccion('Error al eliminar: ' + e.message);
    } finally {
      setTimeout(()=> setAccionFila({id:null, tipo:null}), 300);
      setTimeout(()=> setMensajeAccion(null), 5000);
    }
  };

  return (
    <div className="reportes-container">
      <h2> Reportes y Estadísticas</h2>
      <h3><p>Bienvenido, {usuario.nombre}. Aquí podrás ver las estadísticas de ventas, inventario y pedidos.</p></h3> 

      {cargando && <p>Cargando datos...</p>}
      {error && <p style={{color:'red'}}>Error: {error}</p>}

      <div className="stats-grid1">
        <div className="stat-card1">
          <h3>Total Ventas</h3>
          <p>{fmtMoneda.format(totalVentas)}</p>
        </div>
        <div className="stat-card1">
          <h3>Productos</h3>
          <p>{fmtNumero.format(totalProductos)}</p>
        </div>
        <div className="stat-card1">
          <h3>Unidades Totales</h3>
          <p>{fmtNumero.format(totalStock)}</p>
        </div>
        <div className="stat-card1">
          <h3>Valor Inventario</h3>
          <p>{fmtMoneda.format(totalValorInventario)}</p>
        </div>
        <div className="stat-card1">
          <h3>Ticket Promedio</h3>
          <p>{fmtMoneda.format(ticketPromedio)}</p>
        </div>
        <div className="stat-card1">
          <h3>Ventas Hoy</h3>
          <p>{fmtMoneda.format(totalVentasHoy)}</p>
        </div>
      </div>

      <div className="reportes-paneles">
        <div className="panel">
          <h3>Top 5 por Valor</h3>
          {topValor.length === 0 && <p style={{opacity:.7}}>No hay datos</p>}
          <ul className="lista-top">
            {topValor.map(t => (
              <li key={t.id} className="top-item">
                <div className="top-info">
                  <strong>{t.concepto || 'Sin nombre'}</strong>
                  <span className="codigo">({t.codigo || '—'})</span>
                  <span className="valor">{fmtMoneda.format(t.valor)}</span>
                </div>
                <div className="barra-outer">
                  <div className="barra-inner" style={{width: maxValor? (t.valor / maxValor * 100)+'%':'0%'}} />
                </div>
                <small>{fmtNumero.format(t.cantidad)} u.</small>
              </li>
            ))}
          </ul>
        </div>
        <div className="panel">
          <h3>Distribución por Stock</h3>
          {distribucion.map(d => (
            <div key={d.label} className="dist-row">
              <span className="dist-label">{d.label}</span>
              <div className="barra-outer small">
                <div className="barra-inner" style={{width: maxDist? (d.count / maxDist * 100)+'%':'0%'}} />
              </div>
              <span className="dist-count">{fmtNumero.format(d.count)}</span>
            </div>
          ))}
        </div>
        <div className="panel">
          <h3>Próximas a vencer (7 días)</h3>
          {!proximas && <p style={{opacity:.7}}>Cargando...</p>}
          {proximas && (
            proximas.cotizaciones?.length ? (
              <table className="tabla-ventas">
                <thead><tr><th>Folio</th><th>Cliente</th><th>Vence</th></tr></thead>
                <tbody>
                  {proximas.cotizaciones.map(c => (
                    <tr key={c.id_cotizacion}>
                      <td>{c.folio}</td>
                      <td>{c.cliente || '—'}</td>
                        <td>{(() => {
                          if (!c.vigente_hasta) return '—';
                          const fecha = new Date(c.vigente_hasta + 'T00:00:00');
                          return isNaN(fecha.getTime()) ? '—' : fecha.toLocaleDateString('es-MX');
                        })()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p style={{opacity:.7}}>Sin próximas a vencer</p>
          )}
        </div>
      </div>

      <div style={{marginTop:'25px'}}>
        <h3>Resumen Rápido</h3>
        <ul style={{lineHeight:1.6}}>
          <h3><li>Total de productos distintos: {fmtNumero.format(totalProductos)}</li></h3>
          <h3><li>Unidades acumuladas (stock): {fmtNumero.format(totalStock)}</li></h3>
          <h3><li>Valor estimado del inventario: {fmtMoneda.format(totalValorInventario)}</li></h3>
          <h3><li>Ventas registradas: {fmtNumero.format(ventas.length)}</li></h3>
          <h3><li>Ticket promedio: {fmtMoneda.format(ticketPromedio)}</li></h3>
        </ul>
      </div>

      <div style={{marginTop:'30px'}}>
        <div style={{display:'flex', alignItems:'center', gap:12}}>
          <h3 style={{margin:0}}>Ventas Recientes</h3>
          {ventas.length>0 && <button onClick={imprimirResumenVentas}>Imprimir Resumen</button>}
        </div>
        {ventas.length === 0 && <p style={{opacity:.7}}>No hay ventas registradas aún.</p>}
        {ventas.length > 0 && (
          <div style={{overflowX:'auto'}}>
            <table className="tabla-ventas">
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Fecha</th>
                  <th>Total</th>
                  <th>Método</th>
                  <th>Subtotal Neto</th>
                  <th>Descuento</th>
                  <th>IVA</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {ventas.map(v => {
                  const fechaFmt = new Date(v.fecha).toLocaleString('es-MX');
                  const cargandoEsta = accionFila.id === v.id_venta;
                  return (
                    <tr key={v.id_venta}>
                      <td>{v.id_venta}</td>
                      <td>{fechaFmt}</td>
                      <td>{fmtMoneda.format(v.total || 0)}</td>
                      <td>{v.metodo_pago}</td>
                      <td>{fmtMoneda.format(v.subtotal_neto || 0)}</td>
                      <td>{fmtMoneda.format(v.descuento_total || 0)}</td>
                      <td>{fmtMoneda.format(v.iva || 0)}</td>
                      <td>
                        <button
                          style={{marginRight:4}}
                          disabled={cargandoEsta}
                          onClick={()=>{ setAccionFila({id:v.id_venta, tipo:'ver'}); cargarDetalle(v.id_venta); }}>
                          {cargandoEsta && accionFila.tipo==='ver' ? '...' : 'Ver'}
                        </button>
                        <button
                          disabled={cargandoEsta}
                          onClick={async ()=>{
                            setAccionFila({id:v.id_venta, tipo:'imprimir'});
                            // Abrir ventana antes del fetch para evitar bloqueo por popup blocker
                            const preWin = window.open('', 'PRINT_'+v.id_venta, 'height=650,width=400');
                            if(!preWin){ setMensajeAccion('Bloqueado por el navegador, permite popups.'); return; }
                            preWin.document.write('<html><head><title>Cargando...</title></head><body><p style="font-family:monospace">Generando ticket...</p></body></html>');
                            preWin.document.close();
                            const data = await cargarDetalle(v.id_venta,{soloRetornar:true});
                            if (data) {
                              // Reutilizamos imprimirTicket pero pasándole la ventana ya abierta
                              const { venta, detalle } = data;
                              const fecha = new Date(venta.fecha).toLocaleString('es-MX');
                              const linea = (txt='') => `<div class="ln">${txt}</div>`;
                              const fmt = (n) => new Intl.NumberFormat('es-MX', { style:'currency', currency:'MXN' }).format(Number(n)||0);
                              const totalCant = detalle.reduce((a,d)=> a + d.cantidad, 0);
                              preWin.document.open();
                              preWin.document.write(`<!DOCTYPE html><html><head><title>Ticket #${venta.id_venta}</title>
                                <style>
                                  body{ font-family:monospace; margin:0; padding:10px; }
                                  .ticket{ width:270px; }
                                  h1{ font-size:16px; text-align:center; margin:0 0 6px; }
                                  .center{ text-align:center; }
                                  .sep{ border-top:1px dashed #000; margin:6px 0; }
                                  .ln{ display:flex; justify-content:space-between; font-size:12px; }
                                  .item{ font-size:12px; margin-bottom:4px; }
                                  .item-name{ display:block; }
                                  .totales .ln{ font-weight:bold; }
                                  .small{ font-size:11px; }
                                </style>
                              </head><body onload="window.print(); setTimeout(()=>window.close(), 400);">
                                <div class="ticket">
                                  <h1>SAFETYPOINT</h1>
                                  <div class="center small">RFC: N/A</div>
                                  <div class="center small">Fecha: ${fecha}</div>
                                  <div class="center small">Folio: ${venta.id_venta}</div>
                                  <div class="sep"></div>
                                  ${detalle.map(d=>{
                                    const unit = (Number(d.precio_unitario) - Number(d.descuento_unitario));
                                    const imp = unit * d.cantidad;
                                    return `<div class=\"item\"><span class=\"item-name\">${d.concepto}</span>`+
                                      `<span>${d.cantidad} x ${unit.toFixed(2)} = ${imp.toFixed(2)}</span></div>`;
                                  }).join('')}
                                  <div class="sep"></div>
                                  <div class="totales">
                                    ${linea(`Articulos:${totalCant}`)}
                                    ${linea(`Sub Bruto:${fmt(venta.subtotal_bruto)}`)}
                                    ${linea(`Desc:${fmt(venta.descuento_total)}`)}
                                    ${linea(`Sub Neto:${fmt(venta.subtotal_neto)}`)}
                                    ${linea(`IVA:${fmt(venta.iva)}`)}
                                    ${linea(`TOTAL:${fmt(venta.total)}`)}
                                    ${venta.recibido != null ? linea(`Recibido:${fmt(venta.recibido)}`):''}
                                    ${venta.cambio != null ? linea(`Cambio:${fmt(venta.cambio)}`):''}
                                    ${linea(`Método:${venta.metodo_pago}`)}
                                  </div>
                                  <div class="sep"></div>
                                  <div class="center small">Gracias por su compra</div>
                                </div>
                              </body></html>`);
                              preWin.document.close();
                              preWin.focus();
                            } else {
                              preWin.close();
                            }
                          }}>
                          {cargandoEsta && accionFila.tipo==='imprimir' ? '...' : 'Imprimir'}
                        </button>
                        <button
                          style={{background:'#b30000', color:'#fff', marginLeft:4}}
                          disabled={cargandoEsta}
                          onClick={()=> eliminarVenta(v)}>
                          {cargandoEsta && accionFila.tipo==='eliminar' ? '...' : 'Eliminar'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      {mensajeAccion && <div style={{marginTop:10, fontSize:'.75rem', color:'#b00'}}>{mensajeAccion}</div>}
      </div>

      {ventaSeleccionada && (
        <div className="modal-detalle-venta" onClick={cerrarDetalle}>
          <div className="modal-content" onClick={e=>e.stopPropagation()}>
            <h3>Venta #{ventaSeleccionada.venta.id_venta}</h3>
            {detalleCargando && <p>Cargando detalle...</p>}
            {detalleError && <p style={{color:'red'}}>{detalleError}</p>}
            {!detalleCargando && !detalleError && (
              <>
                <p><strong>Fecha:</strong> {new Date(ventaSeleccionada.venta.fecha).toLocaleString('es-MX')}</p>
                <p><strong>Método:</strong> {ventaSeleccionada.venta.metodo_pago}</p>
                <table className="tabla-detalle">
                  <thead>
                    <tr>
                      <th>Concepto</th>
                      <th>Cant</th>
                      <th>PU</th>
                      <th>Desc U</th>
                      <th>Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ventaSeleccionada.detalle.map(d => {
                      const unit = (Number(d.precio_unitario) - Number(d.descuento_unitario));
                      return (
                        <tr key={d.id_detalle}>
                          <td>{d.concepto}</td>
                          <td>{d.cantidad}</td>
                          <td>{fmtMoneda.format(d.precio_unitario)}</td>
                          <td>{fmtMoneda.format(d.descuento_unitario)}</td>
                          <td>{fmtMoneda.format(unit * d.cantidad)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="resumen-totales">
                  <div>Subtotal Bruto: {fmtMoneda.format(ventaSeleccionada.venta.subtotal_bruto)}</div>
                  <div>Descuento Total: {fmtMoneda.format(ventaSeleccionada.venta.descuento_total)}</div>
                  <div>Subtotal Neto: {fmtMoneda.format(ventaSeleccionada.venta.subtotal_neto)}</div>
                  <div>IVA: {fmtMoneda.format(ventaSeleccionada.venta.iva)}</div>
                  <div><strong>Total: {fmtMoneda.format(ventaSeleccionada.venta.total)}</strong></div>
                </div>
                <div style={{marginTop:10}}>
                  <button onClick={()=>imprimirTicket(ventaSeleccionada)}>Imprimir Ticket</button>{' '}
                  <button onClick={cerrarDetalle}>Cerrar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Reportes;
